import { useEffect, useState } from "react";
import { useCentralStore } from "../../../store/Central";
import { Region } from "../../../types/enums";
import { CountryEnum } from "@/lib/supabase/supabaseEnums";
import { CustomTextFieldShadcn } from "@/dentlab/src/forms/form-input-components/custom-input-components/custom-text-field-shadcn.component";
import { ONBOARDING_STATUS, ONBOARDING_STEPS } from "@/dentlab/src/constants";
import { useAttributesStore } from "@/dentlab/src/store/Attributes";
import { CountrySelectShadcn } from "@/dentlab/src/forms/form-input-components/selects/country-select/country.select";
import { OnboardingStep } from "../onboarding-components/onboarding-step";
import { validateIban, validateReferenceNumber } from "../onboarding-validations";
import { QrPaymentSlipMockup } from "../onboarding-components/onboarding-mockups/QrPaymentSlipMockupProps";
import { Button } from "@/dentlab/src/components/ui/button";
import { StepProps } from "./types";

interface BankAccountValidationParams {
    accountName: string;
    qrIban: string;
    referenceNumber: string;
    cdtrName: string;
    cdtrStreet: string;
    cdtrHouseNumber: string;
    cdtrCity: string;
    cdtrPostalCode: string;
}

const validateBankAccount = (params: BankAccountValidationParams): string | null => {
    if (!params.accountName.trim()) return "Bitte geben Sie einen Kontonamen ein";
    if (!params.qrIban.trim() || !validateIban(params.qrIban)) return "Bitte geben Sie eine gültige IBAN ein";
    if (!validateReferenceNumber(params.referenceNumber)) return "Bitte geben Sie eine gültige Referenznummer ein (13 Ziffern)";
    if (!params.cdtrName.trim()) return "Bitte geben Sie den Namen des Kontoinhabers ein";
    if (!params.cdtrStreet.trim()) return "Bitte geben Sie die Strasse des Kontoinhabers ein";
    if (!params.cdtrHouseNumber.trim()) return "Bitte geben Sie die Hausnummer des Kontoinhabers ein";
    if (!params.cdtrCity.trim()) return "Bitte geben Sie den Ort des Kontoinhabers ein";
    if (!params.cdtrPostalCode.trim()) return "Bitte geben Sie die PLZ des Kontoinhabers ein";
    return null;
};

interface StepBankAccountProps extends StepProps {
    region: Region;
}

export const StepBankAccount: React.FC<StepBankAccountProps> = ({ onComplete, stepNumber, totalSteps, canGoBack, onGoBack, region }) => {
    const {
        organization,
        bankAccountsLookup,
        createOrUpdateOnboardingBankAccount,
    } = useCentralStore((state) => ({
        organization: state.organization,
        bankAccountsLookup: state.bankAccountsLookup,
        createOrUpdateOnboardingBankAccount: state.createOrUpdateOnboardingBankAccount,
    }));
    const { setOnboardingAttribute } = useAttributesStore((state) => ({
        setOnboardingAttribute: state.setOnboardingAttribute,
    }));

    const [bankName, setBankName] = useState("");
    const [bankCity, setBankCity] = useState("");
    const [qrIban, setQrIban] = useState("");
    const [accountName, setAccountName] = useState("");
    const [referenceNumber, setReferenceNumber] = useState("");

    // Account holder address (optional)
    const [cdtrName, setCdrName] = useState("");
    const [cdtrStreet, setCdrStreet] = useState("");
    const [cdtrHouseNumber, setCdrHouseNumber] = useState("");
    const [cdtrCity, setCdrCity] = useState("");
    const [cdtrPostalCode, setCdrPostalCode] = useState("");
    const [cdtrCountry, setCdrCountry] = useState<CountryEnum>(CountryEnum.SWITZERLAND);

    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!organization) {
            return;
        }
        if (Object.keys(bankAccountsLookup).length === 0 && organization.default_bank_account_id !== null) {
            // Pre-fill address with organization data if not already filled
            const regex = organization.street?.match(/(\D+)\s+(\d+)/)?.groups;
            if (!cdtrName) {
                setCdrName(organization.name || "");
                setCdrStreet(regex?.[1] || "");
                setCdrHouseNumber(regex?.[2] || "");
                setCdrCity(organization.city || "");
                setCdrPostalCode(organization.postal_code || "");
                setCdrCountry((organization.country as CountryEnum) || CountryEnum.SWITZERLAND);
            }
        } else {
            const defaultBank = bankAccountsLookup[organization.default_bank_account_id || ""];
            if (defaultBank) {
                setBankName(defaultBank.bank_name || "");
                setBankCity(defaultBank.bank_city || "");
                setQrIban(defaultBank.qr_iban || "");
                setAccountName(defaultBank.account_name || "");
                setReferenceNumber(defaultBank.reference_number || "");
                setCdrName(defaultBank.cdtr_name || organization.name || "");
                setCdrStreet(defaultBank.cdtr_street_line_1 || "");
                setCdrHouseNumber(defaultBank.cdtr_house_number_line_2 || "");
                setCdrCity(defaultBank.cdtr_city || organization.city || "");
                setCdrPostalCode(defaultBank.cdtr_area_code || organization.postal_code || "");
                setCdrCountry((defaultBank.cdtr_country as CountryEnum) || (organization.country as CountryEnum) || CountryEnum.SWITZERLAND);
            } else {
                const regex = organization.street?.match(/(\D+)\s+(\d+)/);
                if (!cdtrName) {
                    setCdrName(organization.name || "");
                    setCdrStreet(regex?.[1] || "");
                    setCdrHouseNumber(regex?.[2] || "");
                    setCdrCity(organization.city || "");
                    setCdrPostalCode(organization.postal_code || "");
                    setCdrCountry((organization.country as CountryEnum) || CountryEnum.SWITZERLAND);
                }
            }
        }
    }, []);


    const handleSubmit = async () => {
        const error = validateBankAccount({ accountName, qrIban, referenceNumber, cdtrName, cdtrStreet, cdtrHouseNumber, cdtrCity, cdtrPostalCode });
        if (error) {
            setErrorMessage(error);
            return;
        }

        // Create/update bank account and save progress
        const bankId = await createOrUpdateOnboardingBankAccount({
            account_name: accountName || null,
            bank_name: bankName || null,
            bank_city: bankCity || null,
            qr_iban: qrIban || null,
            reference_number: referenceNumber || null,
            cdtr_name: cdtrName || null,
            cdtr_street: cdtrStreet || null,
            cdtr_house_number: cdtrHouseNumber || null,
            cdtr_city: cdtrCity || null,
            cdtr_postal_code: cdtrPostalCode || null,
            cdtr_country: cdtrCountry || null,
        });

        if (bankId) {
            await setOnboardingAttribute(ONBOARDING_STEPS.BANK_ACCOUNT, ONBOARDING_STATUS.COMPLETED, 'org');
            onComplete();
        }
    }

    const handleSkipWithMockup = async () => {
        setLoading(true);
        const bankId = await createOrUpdateOnboardingBankAccount({
            account_name: "Hauptkonto Muster",
            bank_name: "Musterbank Muster",
            bank_city: "Musterstadt",
            qr_iban: "CH4431999123000889012",
            reference_number: "0000000000000",
            cdtr_name: "Muster GmbH Muster",
            cdtr_street: "Musterstraße 1",
            cdtr_house_number: "1",
            cdtr_city: "Musterstadt",
            cdtr_postal_code: "1234",
            cdtr_country: CountryEnum.SWITZERLAND,
        });

        if (bankId) {
            await setOnboardingAttribute(ONBOARDING_STEPS.BANK_ACCOUNT, ONBOARDING_STATUS.COMPLETED, 'org');
            onComplete();
        }
        setLoading(false);
    }
    return (
        <OnboardingStep
            stepTitle="Bankkonto"
            stepDescription="Hinterlegen Sie Ihre Bankverbindung für QR-Rechnungen."
            onButtonClick={handleSubmit}
            stepNumber={stepNumber}
            totalSteps={totalSteps}
            canGoBack={canGoBack}
            onGoBackClick={onGoBack}
            buttonText="Weiter"
            buttonDisabled={false}
            loading={loading}
            errorMessage={errorMessage}
            onErrorMessageClose={() => setErrorMessage(null)}
            rightPanel={
                <QrPaymentSlipMockup
                    iban={qrIban}
                    referenceNumber={referenceNumber}
                    cdtrName={cdtrName}
                    cdtrStreet={cdtrStreet}
                    cdtrHouseNumber={cdtrHouseNumber}
                    cdtrPostalCode={cdtrPostalCode}
                    cdtrCity={cdtrCity}
                    cdtrCountry={cdtrCountry}
                />
            }
        >
            <div className="space-y-2 sm:space-y-3">
                {/* Row 1: Kontoname + Name des Kontoinhabers */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-start">
                    <CustomTextFieldShadcn
                        label="Kontoname"
                        id="accountName"
                        name="accountName"
                        value={accountName}
                        onChange={(e) => setAccountName(e.target.value)}
                        classNameInput="h-9 sm:h-10"
                        placeholder="Hauptkonto"
                        data-testid="bank-account-name-input"
                        required
                        tabIndex={1}
                    />
                    <CustomTextFieldShadcn
                        label="Name des Kontoinhabers"
                        id="cdtrName"
                        name="cdtrName"
                        value={cdtrName}
                        onChange={(e) => setCdrName(e.target.value)}
                        classNameInput="h-9 sm:h-10"
                        required
                        data-testid="bank-cdtr-name-input"
                        tabIndex={6}
                    />
                </div>

                {/* Row 2: IBAN/Bank + Adresse */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-start">
                    <div className="space-y-2 sm:space-y-3">
                        <CustomTextFieldShadcn
                            label={region === Region.CH ? "QR-IBAN" : "IBAN"}
                            id="qrIban"
                            name="qrIban"
                            value={qrIban}
                            onChange={(e) => setQrIban(e.target.value)}
                            classNameInput="h-9 sm:h-10"
                            placeholder={
                                region === Region.CH
                                    ? "CH93 0076 2011 6238 5295 7"
                                    : "DE89 3704 0044 0532 0130 00"
                            }
                            customHelperText={
                                region === Region.CH
                                    ? "Format: CH gefolgt von 19 Ziffern"
                                    : "Format: DE gefolgt von 20 Ziffern"
                            }
                            required
                            data-testid="bank-iban-input"
                            tabIndex={2}
                        />
                        <CustomTextFieldShadcn
                            label="Referenznummer"
                            id="referenceNumber"
                            name="referenceNumber"
                            value={referenceNumber}
                            onChange={(e) => setReferenceNumber(e.target.value)}
                            classNameInput="h-9 sm:h-10"
                            placeholder="0000000000000"
                            customHelperText="13 Ziffern, falls nicht vorhanden, 0000000000000 eintragen"
                            data-testid="bank-reference-number-input"
                            required
                            tabIndex={3}
                        />
                        <CustomTextFieldShadcn
                            label="Bankname"
                            id="bankName"
                            name="bankName"
                            value={bankName}
                            onChange={(e) => setBankName(e.target.value)}
                            classNameInput="h-9 sm:h-10"
                            data-testid="bank-name-input"
                            tabIndex={4}
                        />
                        <CustomTextFieldShadcn
                            label="Bankort"
                            id="bankCity"
                            name="bankCity"
                            value={bankCity}
                            onChange={(e) => setBankCity(e.target.value)}
                            classNameInput="h-9 sm:h-10"
                            data-testid="bank-city-input"
                            tabIndex={5}
                        />
                    </div>
                    <div className="space-y-2 sm:space-y-3">
                        <CustomTextFieldShadcn
                            label="Strasse"
                            id="cdtrStreet"
                            name="cdtrStreet"
                            value={cdtrStreet}
                            onChange={(e) => setCdrStreet(e.target.value)}
                            classNameInput="h-9 sm:h-10"
                            required
                            data-testid="bank-cdtr-street-input"
                            tabIndex={7}
                        />
                        <CustomTextFieldShadcn
                            label="Hausnummer"
                            id="cdtrHouseNumber"
                            name="cdtrHouseNumber"
                            value={cdtrHouseNumber}
                            onChange={(e) => setCdrHouseNumber(e.target.value)}
                            classNameInput="h-9 sm:h-10"
                            required
                            data-testid="bank-cdtr-house-number-input"
                            tabIndex={8}
                        />
                        <CustomTextFieldShadcn
                            label="Ort"
                            id="cdtrCity"
                            name="cdtrCity"
                            value={cdtrCity}
                            onChange={(e) => setCdrCity(e.target.value)}
                            classNameInput="h-9 sm:h-10"
                            required
                            data-testid="bank-cdtr-city-input"
                            tabIndex={9}
                        />
                        <CustomTextFieldShadcn
                            label="PLZ"
                            id="cdtrPostalCode"
                            name="cdtrPostalCode"
                            value={cdtrPostalCode}
                            onChange={(e) => setCdrPostalCode(e.target.value)}
                            classNameInput="h-9 sm:h-10"
                            required
                            data-testid="bank-cdtr-postalcode-input"
                            tabIndex={10}
                        />
                        <CountrySelectShadcn
                            onChange={(value) =>
                                setCdrCountry(value as CountryEnum)
                            }
                            value={cdtrCountry}
                            required
                            triggerClassName="w-full h-9 sm:h-10"
                            tabIndex={11}
                        />
                    </div>
                </div>

                <Button
                    type="button"
                    variant="outline"
                    onClick={handleSkipWithMockup}
                    disabled={loading}
                    className="w-full mt-4 text-gray-500"
                    data-testid="bank-account-mockup-skip"
                    aria-label="Testdaten eintragen und überspringen"
                >
                    Testdaten eintragen und überspringen
                </Button>
            </div>
        </OnboardingStep>
    )
}